import React, { useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import ApiClient from '../utils/ApiClient';

// Set base URL dynamically based on environment
const BASE_URL =
    process.env.NODE_ENV === 'development'
        ? 'http://localhost:8080'
        : 'https://calibrate.io';

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            // Prepare the login request payload
            const requestBody = {
                username: email,
                password: password,
            };

            console.log('Attempting login...', { username: email });

            // Call the login endpoint
            const response = await axios.post(`${BASE_URL}/api/login`, requestBody, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            // Extract tokens from response
            const { token, refreshToken } = response.data;
            console.log('Login successful, tokens received');

            if (!token || !refreshToken) {
                throw new Error('Invalid server response - missing tokens');
            }

            // Store both tokens using ApiClient helper
            ApiClient.setAuthTokens(token, refreshToken);

            // Start automatic token refresh
            ApiClient.startTokenRefresh();

            // Decode and verify the JWT
            const decoded = jwtDecode(token);
            console.log('Token decoded, checking roles...');

            if (!decoded || !decoded.roles) {
                throw new Error('Invalid token structure');
            }

            const roles = decoded.roles || [];
            console.log('User roles:', roles);

            // Redirect based on roles (without ROLE_ prefix)
            if (roles.includes('ADMIN')) {
                console.log('Role Admin determined');
                window.location.href = '/admin-dashboard';
            } else if (roles.includes('CASE_REVIEWER')) {
                console.log('Role Case Reviewer determined');
                window.location.href = '/case-review';
            } else {
                throw new Error('No valid roles found');
            }

        } catch (err) {
            console.error('Login error:', err);

            // Clear any existing tokens on login failure
            ApiClient.clearAuth();

            // Set appropriate error message
            if (err.response?.status === 401) {
                setError('Invalid email or password');
            } else if (err.message === 'No valid roles found') {
                setError('Your account does not have the required permissions');
            } else {
                setError('Login failed. Please try again.');
            }

        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <img
                    alt="Calibrate"
                    src="/calibrate-logo.svg"
                    className="mx-auto h-10 w-auto"
                />
                <h2 className="mt-6 text-center text-2xl font-bold tracking-tight text-gray-900">
                    Sign in to your Calibrate account
                </h2>
            </div>

            <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
                <div className="bg-white px-6 py-12 shadow sm:rounded-lg sm:px-12">
                    {error && (
                        <div className="mb-4 rounded-md bg-red-50 p-4">
                            <div className="flex">
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-red-800">
                                        {error}
                                    </h3>
                                </div>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-900">
                                Email address
                            </label>
                            <div className="mt-2">
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    autoComplete="email"
                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-900">
                                Password
                            </label>
                            <div className="mt-2">
                                <input
                                    id="password"
                                    name="password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    autoComplete="current-password"
                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <input
                                    id="remember-me"
                                    name="remember-me"
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                />
                                <label htmlFor="remember-me" className="ml-3 block text-sm text-gray-900">
                                    Remember me
                                </label>
                            </div>

                            <div className="text-sm">
                                <a href="#" className="font-semibold text-indigo-600 hover:text-indigo-500">
                                    Forgot password?
                                </a>
                            </div>
                        </div>

                        <div>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className={`flex w-full justify-center rounded-md px-3 py-1.5 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 
                                    ${isLoading
                                    ? 'bg-indigo-400 cursor-not-allowed'
                                    : 'bg-indigo-600 hover:bg-indigo-500'}`}
                            >
                                {isLoading ? 'Signing in...' : 'Sign in'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}